'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { MessageCircle, X, Send, Loader2, Calendar, Mail } from 'lucide-react';
import { trackScheduleMeetingClick, trackCtaClick, trackLeadFormSubmission, trackChatSession } from '@/lib/analytics';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

const STORAGE_KEY = 'ihq-chat-session';
const PROMPT_DISMISSED_KEY = 'ihq-chat-prompt-dismissed';
const INACTIVITY_DELAY = 30000; // 30 seconds before showing prompt

interface StoredSession {
  sessionId: string;
  identity: string;
  email?: string;
  name?: string;
}

function getStoredSession(): StoredSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
}

function storeSession(sessionId: string, identity: string, email?: string, name?: string) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId, identity, email, name }));
  } catch {
    // Ignore storage errors
  }
}

// Pages where chat widget should appear (sales/conversion focused pages)
const CHAT_ALLOWED_PATHS = [
  '/',                    // Home page
  '/about',               // About page
  '/contact',             // Contact page
  '/services',            // Services listing
  '/industries',          // Industries listing
  '/software',            // Software listing
  '/lp/',                 // Landing pages
];

// Path prefixes where chat should appear
const CHAT_ALLOWED_PREFIXES = [
  '/services/',           // Individual service pages
  '/industries/',         // Individual industry pages
  '/software/',           // Individual software pages
  '/lp/',                 // Landing pages
];

// Pages where chat should NOT appear (content/tool pages have FloatingHelpButton)
const CHAT_EXCLUDED_PREFIXES = [
  '/tools/',              // Tool pages (have FloatingHelpButton)
  '/knowledge-base/',     // KB articles (have FloatingHelpButton)
  '/glossary/',           // Glossary terms
];

interface PageContext {
  pageType: string;
  pageName: string;
  cta: string;
  description: string;
}

/**
 * Get page context for chat bot based on current path
 * This helps the bot understand what the user is looking at and what we want them to do
 */
function getPageContext(pathname: string): PageContext {
  // Home page
  if (pathname === '/') {
    return {
      pageType: 'home',
      pageName: 'Home Page',
      cta: 'Schedule a free consultation',
      description: 'Visitor is on the main landing page exploring our IT and cybersecurity services.',
    };
  }

  // Service pages
  if (pathname === '/services') {
    return {
      pageType: 'services_listing',
      pageName: 'Services Overview',
      cta: 'Help them find the right service for their needs',
      description: 'Visitor is browsing our full list of IT and cybersecurity services.',
    };
  }

  if (pathname.startsWith('/services/')) {
    const serviceSlug = pathname.replace('/services/', '').split('/')[0];
    const serviceName = serviceSlug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return {
      pageType: 'service',
      pageName: serviceName,
      cta: `Schedule a consultation about ${serviceName}`,
      description: `Visitor is viewing our ${serviceName} service page. They may have questions about pricing, implementation, or how it fits their needs.`,
    };
  }

  // Industry pages
  if (pathname === '/industries') {
    return {
      pageType: 'industries_listing',
      pageName: 'Industries Overview',
      cta: 'Help them find industry-specific solutions',
      description: 'Visitor is browsing our industry-specific solutions.',
    };
  }

  if (pathname.startsWith('/industries/')) {
    const industrySlug = pathname.replace('/industries/', '').split('/')[0];
    const industryName = industrySlug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return {
      pageType: 'industry',
      pageName: `${industryName} Industry`,
      cta: `Discuss ${industryName} compliance and security needs`,
      description: `Visitor is viewing our ${industryName} industry page. They likely work in this industry and want solutions tailored to their compliance and security requirements.`,
    };
  }

  // Software pages
  if (pathname === '/software') {
    return {
      pageType: 'software_listing',
      pageName: 'Software Comparisons',
      cta: 'Help them choose the right security software',
      description: 'Visitor is browsing our software comparison guides.',
    };
  }

  if (pathname.startsWith('/software/')) {
    const softwareSlug = pathname.replace('/software/', '').split('/')[0];
    const softwareName = softwareSlug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return {
      pageType: 'software',
      pageName: softwareName,
      cta: `Help them evaluate ${softwareName} for their organization`,
      description: `Visitor is researching ${softwareName}. They may need help with implementation, licensing, or comparing alternatives.`,
    };
  }

  // Landing pages
  if (pathname.startsWith('/lp/')) {
    const lpSlug = pathname.replace('/lp/', '').split('/')[0];
    const lpName = lpSlug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return {
      pageType: 'landing_page',
      pageName: lpName,
      cta: 'Convert them to schedule a call or start an assessment',
      description: `Visitor is on a focused landing page about ${lpName}. This is a high-intent visitor who came from a specific campaign.`,
    };
  }

  // About page
  if (pathname === '/about') {
    return {
      pageType: 'about',
      pageName: 'About Us',
      cta: 'Schedule a call to learn more about working with us',
      description: 'Visitor is learning about our company. They may be evaluating whether to work with us.',
    };
  }

  // Contact page
  if (pathname === '/contact') {
    return {
      pageType: 'contact',
      pageName: 'Contact Page',
      cta: 'Help them get in touch or schedule a meeting',
      description: 'Visitor is on the contact page. High intent - they want to reach out.',
    };
  }

  // Blog posts
  if (pathname.startsWith('/blog/')) {
    const blogSlug = pathname.replace('/blog/', '').split('/')[0];
    const blogTitle = blogSlug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return {
      pageType: 'blog',
      pageName: blogTitle,
      cta: 'Subscribe to our newsletter or schedule a consultation',
      description: `Visitor is reading a blog post about ${blogTitle}. They may be researching this topic for their organization.`,
    };
  }

  // Default
  return {
    pageType: 'general',
    pageName: 'Website',
    cta: 'Schedule a free consultation',
    description: 'Visitor is browsing our website.',
  };
}

function shouldShowChatOnPath(pathname: string): boolean {
  // Check exclusions first
  for (const prefix of CHAT_EXCLUDED_PREFIXES) {
    if (pathname.startsWith(prefix)) {
      return false;
    }
  }

  // Check exact matches
  if (CHAT_ALLOWED_PATHS.includes(pathname)) {
    return true;
  }

  // Check prefix matches
  for (const prefix of CHAT_ALLOWED_PREFIXES) {
    if (pathname.startsWith(prefix)) {
      return true;
    }
  }

  return false;
}

// Check if HubSpot chat widget is visible on the page
function isHubSpotChatVisible(): boolean {
  if (typeof window === 'undefined') return false;

  // HubSpot adds an iframe with this ID when the chat widget is active
  const hubspotFrame = document.getElementById('hubspot-messages-iframe-container');

  // Debug logging
  console.log('[TwilioChat] HubSpot detection:', {
    hubspotFrame: !!hubspotFrame,
    frameHeight: hubspotFrame?.offsetHeight,
    frameDisplay: hubspotFrame ? window.getComputedStyle(hubspotFrame).display : null,
  });

  if (hubspotFrame) {
    const style = window.getComputedStyle(hubspotFrame);
    const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && hubspotFrame.offsetHeight > 0;
    console.log('[TwilioChat] HubSpot visible:', isVisible);
    return isVisible;
  }

  // Also check for the chat launcher button
  const launcher = document.querySelector('[data-test-id="chat-widget-launcher"]');
  console.log('[TwilioChat] HubSpot launcher found:', !!launcher);
  if (launcher) {
    return true;
  }

  console.log('[TwilioChat] No HubSpot detected, showing Twilio widget');
  return false;
}

export default function TwilioChat() {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [identity, setIdentity] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [emailInput, setEmailInput] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [nameInput, setNameInput] = useState<string>('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [hubspotActive, setHubspotActive] = useState(true); // Default to true, then check
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptDismissed, setPromptDismissed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const chatStartTimeRef = useRef<number | null>(null);
  const userMessageCountRef = useRef<number>(0);

  // Check if chat should show on this page
  const showOnThisPage = shouldShowChatOnPath(pathname);

  // Get page context for the chat bot
  const pageContext = getPageContext(pathname);

  // Check HubSpot availability on mount and periodically
  useEffect(() => {
    console.log('[TwilioChat] Component mounted, waiting 3s to check HubSpot...');

    // Initial delay to let HubSpot load
    const initialCheck = setTimeout(() => {
      const isVisible = isHubSpotChatVisible();
      console.log('[TwilioChat] Initial check complete, HubSpot active:', isVisible);
      setHubspotActive(isVisible);
    }, 3000);

    // Periodic check every 30 seconds
    const interval = setInterval(() => {
      const isVisible = isHubSpotChatVisible();
      console.log('[TwilioChat] Periodic check, HubSpot active:', isVisible);
      setHubspotActive(isVisible);
    }, 30000);

    return () => {
      clearTimeout(initialCheck);
      clearInterval(interval);
    };
  }, []);

  // Check if prompt was already dismissed this session
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const dismissed = sessionStorage.getItem(PROMPT_DISMISSED_KEY);
      if (dismissed === 'true') {
        setPromptDismissed(true);
      }
    }
  }, []);

  // Proactive prompt after inactivity
  useEffect(() => {
    // Don't show if: chat is open, prompt dismissed, HubSpot active, or not on allowed page
    if (isOpen || promptDismissed || hubspotActive || !showOnThisPage) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      setShowPrompt(false);
      return;
    }

    const resetTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      // Hide prompt if it's showing and user becomes active
      if (showPrompt) {
        // Don't hide on activity, let them interact with it
        return;
      }
      inactivityTimerRef.current = setTimeout(() => {
        setShowPrompt(true);
      }, INACTIVITY_DELAY);
    };

    // Start the timer
    resetTimer();

    // Reset timer on user activity
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [isOpen, promptDismissed, hubspotActive, showOnThisPage, showPrompt]);

  // Hide prompt when chat opens, track session end when closed
  useEffect(() => {
    if (isOpen) {
      setShowPrompt(false);
    } else if (chatStartTimeRef.current && userMessageCountRef.current > 0) {
      // Chat was closed after having a conversation
      const sessionDuration = Math.round((Date.now() - chatStartTimeRef.current) / 1000);
      trackChatSession('ended', {
        messageCount: messages.length,
        sessionDurationSeconds: sessionDuration,
        userMessageCount: userMessageCountRef.current,
        botMessageCount: messages.filter(m => m.sender === 'bot').length,
      });
    }
  }, [isOpen, messages]);

  const dismissPrompt = useCallback(() => {
    setShowPrompt(false);
    setPromptDismissed(true);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
    }
  }, []);

  const handlePromptClick = useCallback(() => {
    setShowPrompt(false);
    setIsOpen(true);
    trackCtaClick('Chat Prompt: Click', 'inline', '');
  }, []);

  // Initialize session
  useEffect(() => {
    const stored = getStoredSession();
    if (stored) {
      setSessionId(stored.sessionId);
      setIdentity(stored.identity);
      if (stored.email) {
        setEmail(stored.email);
        setEmailSubmitted(true);
      }
      if (stored.name) {
        setName(stored.name);
      }
    } else {
      const newSessionId = `web-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const newIdentity = `visitor-${Math.random().toString(36).slice(2, 9)}`;
      setSessionId(newSessionId);
      setIdentity(newIdentity);
      storeSession(newSessionId, newIdentity);
    }
  }, []);

  // Add welcome message when email is submitted
  useEffect(() => {
    if (isOpen && emailSubmitted && messages.length === 0 && name) {
      // Create context-aware welcome message
      let welcomeText = `Hi ${name}! I'm the Inventive HQ assistant.`;

      switch (pageContext.pageType) {
        case 'service':
          welcomeText += ` I see you're looking at our ${pageContext.pageName} service. Do you have questions about how it works or want to discuss your specific needs?`;
          break;
        case 'industry':
          welcomeText += ` I see you're exploring solutions for the ${pageContext.pageName.replace(' Industry', '')} industry. I can help with compliance requirements, security challenges, or connect you with a specialist.`;
          break;
        case 'software':
          welcomeText += ` I see you're researching ${pageContext.pageName}. I can help you understand features, pricing, or how it compares to alternatives.`;
          break;
        case 'landing_page':
          welcomeText += ` Thanks for your interest! How can I help you learn more or get started?`;
          break;
        case 'contact':
          welcomeText += ` Looking to get in touch? I can answer quick questions or help you schedule a call with our team.`;
          break;
        default:
          welcomeText += ` How can I help you today?`;
      }

      setMessages([
        {
          id: 'welcome',
          text: welcomeText,
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen, emailSubmitted, messages.length, name, pageContext]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus appropriate input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (emailSubmitted) {
          inputRef.current?.focus();
        } else {
          emailInputRef.current?.focus();
        }
      }, 100);
    }
  }, [isOpen, emailSubmitted]);

  // Handle email submission
  const handleEmailSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedEmail = emailInput.trim();
    const trimmedName = nameInput.trim();
    if (!trimmedEmail || !trimmedEmail.includes('@') || !trimmedName) return;

    setEmail(trimmedEmail);
    setName(trimmedName);
    setEmailSubmitted(true);
    // Update identity to use email
    setIdentity(trimmedEmail);
    storeSession(sessionId, trimmedEmail, trimmedEmail, trimmedName);

    // Track the email capture as a lead form submission (includes Clarity identification)
    trackLeadFormSubmission('Chat Widget Email', pageContext.pageType, undefined, trimmedEmail);

    // Track chat session started
    chatStartTimeRef.current = Date.now();
    userMessageCountRef.current = 0;
    trackChatSession('started');

    // Send intro message to Twilio with the user's name and page context
    try {
      const contextMessage = [
        `[SYSTEM CONTEXT - Do not repeat this to the user]`,
        `Visitor: ${trimmedName} (${trimmedEmail})`,
        `Current Page: ${pageContext.pageName}`,
        `Page Type: ${pageContext.pageType}`,
        `Situation: ${pageContext.description}`,
        `Goal: ${pageContext.cta}`,
        `[END CONTEXT]`,
        ``,
        `Hi, my name is ${trimmedName}.`,
      ].join('\n');

      await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: contextMessage,
          sessionId,
          identity: trimmedEmail,
        }),
      });
    } catch {
      // Silently fail - the conversation can still continue
    }
  }, [emailInput, nameInput, sessionId, pageContext]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: input.trim(),
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');

    // Track message sent
    userMessageCountRef.current += 1;
    trackChatSession('message_sent', { userMessageCount: userMessageCountRef.current });
    setIsLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage.text,
          sessionId,
          identity,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const botMessage: Message = {
          id: `bot-${Date.now()}`,
          text: data.response,
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botMessage]);

        // Update session ID if returned
        if (data.sessionId && data.sessionId !== sessionId) {
          setSessionId(data.sessionId);
          storeSession(data.sessionId, identity);
        }
      } else {
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          text: "Sorry, I'm having trouble connecting. Please try again or contact us directly.",
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch {
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        text: "Sorry, I couldn't connect. Please try again later or email us at hello@inventivehq.com",
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, sessionId, identity]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Don't render if not on an allowed page
  if (!showOnThisPage) {
    return null;
  }

  // Don't render if HubSpot chat is active
  console.log('[TwilioChat] Render check - hubspotActive:', hubspotActive);
  if (hubspotActive) {
    console.log('[TwilioChat] HubSpot is active, hiding Twilio widget');
    return null;
  }

  return (
    <>
      {/* Proactive prompt - shows after inactivity */}
      {showPrompt && !isOpen && (
        <div className="fixed bottom-44 right-6 lg:bottom-24 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="relative bg-white rounded-2xl shadow-2xl p-4 max-w-[280px] border border-gray-100">
            {/* Dismiss button */}
            <button
              onClick={dismissPrompt}
              className="absolute -top-2 -left-2 w-7 h-7 bg-white rounded-full shadow-md flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors border border-gray-100"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Logo - positioned to overlap top edge */}
            <div className="absolute -top-6 left-1/2 -translate-x-1/2">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-lg bg-white flex items-center justify-center">
                <img
                  src="/icon.png"
                  alt="Inventive HQ"
                  className="w-8 h-8 object-contain"
                />
              </div>
            </div>

            {/* Message */}
            <button
              onClick={handlePromptClick}
              className="w-full text-left pt-4 cursor-pointer hover:opacity-90 transition-opacity"
            >
              <p className="text-gray-800 text-sm leading-relaxed">
                Got any questions? I&apos;m happy to help.
              </p>
            </button>
          </div>

          {/* Arrow pointing to chat bubble */}
          <div className="absolute -bottom-2 right-8 w-4 h-4 bg-white rotate-45 border-r border-b border-gray-100" />
        </div>
      )}

      {/* Chat bubble button - only shows when HubSpot is offline */}
      <button
        onClick={() => {
          if (!isOpen) {
            trackCtaClick('Chat Bubble: Open', 'inline', '');
          }
          setIsOpen(!isOpen);
        }}
        className="fixed bottom-24 right-6 lg:bottom-6 z-50 w-14 h-14 rounded-full bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center cursor-pointer"
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chat window */}
      {isOpen && (
        <div className="fixed bottom-44 right-6 lg:bottom-24 z-50 w-[360px] max-w-[calc(100vw-48px)] h-[500px] max-h-[calc(100vh-120px)] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] text-white px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Inventive HQ</h3>
              <p className="text-xs text-white">AI Assistant</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Schedule link */}
          <a
            href="https://hs.inventivehq.com/meetings/sean1324/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackScheduleMeetingClick('twilio_chat')}
            className="flex items-center justify-center gap-2 py-2.5 text-sm text-[#2cc3d1] hover:text-[#2813e8] transition-colors border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
          >
            <Calendar className="w-4 h-4" />
            <span>Schedule some time with me</span>
          </a>

          {/* Email capture or Messages */}
          {!emailSubmitted ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Let&apos;s get started
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                Tell us a bit about yourself so we can help you better.
              </p>
              <form onSubmit={handleEmailSubmit} className="w-full max-w-xs">
                <div className="flex flex-col gap-3">
                  <input
                    ref={emailInputRef}
                    type="text"
                    aria-label="Your name"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder="Your name"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2813e8] focus:border-transparent text-sm"
                    required
                  />
                  <input
                    type="email"
                    aria-label="Email address"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2813e8] focus:border-transparent text-sm"
                    required
                  />
                  <button
                    type="submit"
                    disabled={!emailInput.trim() || !emailInput.includes('@') || !nameInput.trim()}
                    className="w-full py-3 rounded-lg bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md transition-shadow text-sm"
                  >
                    Start Chat
                  </button>
                </div>
              </form>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-4 text-center">
                We respect your privacy and won&apos;t spam you.
              </p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      message.sender === 'user'
                        ? 'bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] text-white rounded-br-md'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-bl-md px-4 py-3">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input - only show after email submitted */}
          {emailSubmitted && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-4">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  aria-label="Chat message"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2813e8] focus:border-transparent text-sm"
                  disabled={isLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || isLoading}
                  className="w-10 h-10 rounded-full bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md transition-shadow"
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
